// Used for compressing data channel messages. Do not import directly
// unless there's a ubiquitous use case for string compression. Use the lazy
// version of this module instead to lazy load this module and the underlying
// string-compression dependency.
//
// See https://npm-compare.com/compression,lz-string,lz4,lzutf8,pako for a
// comparision of similar lossless compression packages.
import { compressToBase64, decompressFromBase64 } from 'lz-string';

// base64 charset used for compatibility with Twilio data tracks
import { gracefullyParseJson, gracefullyStringifyJson } from '../utils';

/**
 * Stringifies the object into JSON string and compresses the string
 */
export function gracefullyCompressJson(obj: object): [string, null] | [null, Error] {
  const jsonString = gracefullyStringifyJson(obj);
  if (!jsonString) {
    return [null, new Error('Failed to stringify JSON')];
  }
  try {
    return [compressToBase64(jsonString), null];
  } catch (e) {
    return [null, e as Error];
  }
}

/**
 * Decompresses the string and parses it into a JSON object
 */
export function gracefullyDecompressJson<T>(compressed: string): [T, null] | [null, Error] {
  try {
    const decompressedJsonString = decompressFromBase64(compressed);
    const jsonObject = gracefullyParseJson<T>(decompressedJsonString);

    if (!jsonObject) {
      return [null, new Error('Failed to parse JSON')];
    }
    return [jsonObject, null];
  } catch (e) {
    return [null, e as Error];
  }
}

export type gracefullyDecompressJsonType = typeof gracefullyDecompressJson;
export type gracefullyCompressJsonType = typeof gracefullyCompressJson;
